/**
 * career-opportunity-detail — the entity surface behind `/search/opportunities/:id`.
 *
 * Binds via the `urn` attribute (the router sets it from the codec; listed in
 * `entitySurfaces`, so it MUST carry a valid urn). Deep-linkable COLD: `attach()`
 * hydrates from `/api/opportunities/:id` (+ `/api/organizations/:orgRef` when the
 * posting carries an org) into the opportunity + org stores, then binds the folded
 * views. Renders the full authored brief, the per-dimension match (strength +
 * verbatim quotes + named gaps, AX-6), and the org summary — each with its
 * provenance chip + honest `is-degraded` cue. Bookmark / dismiss / delete are the
 * same optimistic store commands as the list. Card CONTENT is domain data.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import {
  I18nToken,
  OpportunityStoreToken,
  OrgStoreToken,
  RouterToken,
  SessionToken,
  apiGet,
} from "../services"
import type { CareerI18n } from "../../i18n"
import {
  careerStream,
  orgUrn,
  type MatchSignalView,
  type OpportunityCardView,
  type OpportunityStore,
  type OrgStore,
  type OrgSummaryView,
  type ShellSession,
} from "../domain"
import {
  DIMENSION_KEY,
  FIT_KEY,
  JOB_STATE_KEY,
  STAGE_KEY,
  opportunityShortId,
  pct,
  tierChip,
} from "../lib/opportunity-card"

interface OpportunityWithMatch {
  opportunity: Record<string, unknown>
  match: Record<string, unknown> | null
}
interface OrgWithSummary {
  org: Record<string, unknown> | null
  summary: Record<string, unknown> | null
}

export class OpportunityDetailVm implements ViewModel {
  readonly card: ReadonlySignal<OpportunityCardView | undefined>
  readonly org: Signal<OrgSummaryView | null> = signal<OrgSummaryView | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  readonly urn: Urn
  private readonly shortId: string
  private seq = 1

  constructor(
    private readonly store: OpportunityStore,
    private readonly orgStore: OrgStore,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
    urn: Urn,
  ) {
    this.t = i18n.t
    this.urn = urn
    this.shortId = opportunityShortId(urn)
    this.card = store.entity(urn)
    this.renderDeps = [this.card, this.org, this.error, this.loaded, store.pending]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.hydrate()
    return () => {}
  }

  bookmark(): void {
    void this.store.command({ type: "bookmark", payload: { opportunityId: this.urn } })
  }
  dismiss(): void {
    void this.store.command({ type: "dismiss", payload: { opportunityId: this.urn } })
  }
  remove(): void {
    void this.store.command({ type: "delete", payload: { opportunityId: this.urn } })
  }
  go(url: string): void {
    void this.router.navigate(url)
  }

  private async hydrate(): Promise<void> {
    const userId = this.userId
    try {
      const { opportunity, match } = await apiGet<OpportunityWithMatch>(`/api/opportunities/${this.shortId}`)
      if (userId && opportunity && typeof opportunity["id"] === "string") {
        const streamId = careerStream(userId, "Opportunity", opportunity["id"])
        this.store.apply({ streamId, version: this.seq++, type: "opportunity.Discovered", payload: { owner: userId, opportunity } })
        if (match) {
          this.store.apply({ streamId, version: this.seq++, type: "opportunity.Matched", payload: { owner: userId, opportunityId: opportunity["id"], match } })
        }
        const orgRef = opportunity["orgRef"]
        if (typeof orgRef === "string" && orgRef.length > 0) await this.hydrateOrg(userId, orgRef)
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  private async hydrateOrg(userId: string, orgRef: string): Promise<void> {
    try {
      const { org, summary } = await apiGet<OrgWithSummary>(`/api/organizations/${encodeURIComponent(orgRef)}`)
      if (!org) return
      const streamId = careerStream(userId, "Organization", orgRef)
      this.orgStore.apply({
        streamId,
        version: this.seq++,
        type: "organization.Observed",
        payload: { owner: userId, orgId: orgRef, name: org["name"] ?? "", evidence: org["evidence"] ?? [], observedAt: org["firstSeenAt"] ?? "" },
      })
      this.orgStore.apply({
        streamId,
        version: this.seq++,
        type: "organization.Researched",
        payload: { owner: userId, evidence: org["evidence"] ?? [], ...(org["register"] ? { register: org["register"] } : {}) },
      })
      if (summary) {
        this.orgStore.apply({ streamId, version: this.seq++, type: "organization.Summarized", payload: { owner: userId, summary } })
      }
      this.org.set(this.orgStore.entity(orgUrn(orgRef)).value ?? null)
    } catch {
      // org dossier is optional — leave the panel empty on failure.
    }
  }
}

export class CareerOpportunityDetail extends KitElement<OpportunityDetailVm> {
  protected createViewModel(ctx: KitContext): OpportunityDetailVm {
    const { urn } = this
    if (!urn) throw new Error("[career] <career-opportunity-detail> needs a urn attribute.")
    return new OpportunityDetailVm(
      ctx.inject(OpportunityStoreToken),
      ctx.inject(OrgStoreToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
      urn,
    )
  }

  private matchSignal(vm: OpportunityDetailVm, s: MatchSignalView): TemplateResult {
    const label = DIMENSION_KEY[s.dimension] ?? "opportunity.dimension.fit"
    return html`
      <li class="match-signal">
        <p class="match-dimension" lang="de">
          <span class="match-dimension-name">${vm.t(label)}</span>
          <span class="match-strength chip" lang="de">${vm.t("opportunity.match.strength", { n: pct(s.strength) })}</span>
        </p>
        ${s.quotes.length > 0
          ? html`<ul class="match-quotes" role="list">${s.quotes.map((q) => html`<li class="match-quote" lang="de">„${q}“</li>`)}</ul>`
          : nothing}
        ${s.gaps.length > 0
          ? html`<p class="match-gaps" lang="de"><span class="match-gaps-label">${vm.t("opportunity.match.gaps")}:</span> ${s.gaps.join(", ")}</p>`
          : nothing}
      </li>
    `
  }

  private matchPanel(vm: OpportunityDetailVm, o: OpportunityCardView): TemplateResult | typeof nothing {
    if (o.signals.length === 0) return nothing
    return html`
      <section class="opportunity-match surface-panel" aria-labelledby="opportunity-match-title">
        <h2 id="opportunity-match-title" class="surface-section-title" lang="de">${vm.t("opportunity.match.title")}</h2>
        ${tierChip(o, vm.t)}
        <ul class="match-signals" role="list">${o.signals.map((s) => this.matchSignal(vm, s))}</ul>
      </section>
    `
  }

  private briefPanel(vm: OpportunityDetailVm, o: OpportunityCardView): TemplateResult | typeof nothing {
    if (!o.brief) return nothing
    const b = o.brief
    return html`
      <section class=${classMap({ "opportunity-brief": true, "surface-panel": true, "is-degraded": b.degraded })} aria-labelledby="opportunity-brief-title">
        <h2 id="opportunity-brief-title" class="surface-section-title" lang="de">${vm.t("opportunity.brief.title")}</h2>
        ${b.degraded ? html`<p class="opportunity-degraded-cue" role="status" lang="de">${vm.t("opportunity.brief.degraded")}</p>` : nothing}
        <p class="opportunity-brief-text" lang="de">${b.brief}</p>
        ${b.fitNote ? html`<p class="opportunity-fitnote" lang="de">${b.fitNote}</p>` : nothing}
        <p class="opportunity-brief-meta">
          <span class="opportunity-prov chip" lang="de">${vm.t("review.narrative.provenance", { n: b.provenanceCount })}</span>
        </p>
      </section>
    `
  }

  private orgPanel(vm: OpportunityDetailVm): TemplateResult {
    const org = vm.org.value
    return html`
      <section class=${classMap({ "opportunity-org": true, "surface-panel": true, "is-degraded": Boolean(org?.degraded) })} aria-labelledby="opportunity-org-title">
        <h2 id="opportunity-org-title" class="surface-section-title" lang="de">${vm.t("org.summary.title")}</h2>
        ${org?.summaryBody
          ? html`
              ${org.name ? html`<p class="opportunity-org-name" lang="de">${org.name}</p>` : nothing}
              <p class="opportunity-org-summary" lang="de">${org.summaryBody}</p>
              <p class="opportunity-org-meta">
                <span class="opportunity-prov chip" lang="de">${vm.t("review.narrative.provenance", { n: org.summaryProvenanceCount })}</span>
                ${org.evidenceCount > 0 ? html`<span class="opportunity-org-evidence chip" lang="de">${vm.t("org.evidence", { n: org.evidenceCount })}</span>` : nothing}
              </p>
            `
          : html`<p class="surface-empty" lang="de">${vm.t("org.summary.empty")}</p>`}
      </section>
    `
  }

  protected render(vm: OpportunityDetailVm): TemplateResult {
    const o = vm.card.value
    const back = html`<a class="opportunity-back chip" href="/search" data-act="nav" data-url="/search" lang="de">${vm.t("opportunity.detail.back")}</a>`

    if (!o || o.dismissed) {
      // Removed/dismissed OR not yet loaded → an honest state (still a valid AX tree).
      const gone = (o?.dismissed ?? false) || (vm.loaded.value && !o)
      return html`
        <section class="surface opportunity-detail" urn=${vm.urn} aria-labelledby="opportunity-detail-title">
          <h1 id="opportunity-detail-title" class="surface-title" lang="de">${vm.t("surface.opportunity.title")}</h1>
          ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: vm.error.value })}</p>` : nothing}
          ${gone ? html`<p class="surface-empty" lang="de">${o?.dismissed ? vm.t("opportunity.detail.removed") : vm.t("opportunity.detail.notFound")}</p>` : nothing}
          ${back}
        </section>
      `
    }

    return html`
      <section
        class=${classMap({ surface: true, "opportunity-detail": true, [`is-${o.stage}`]: true, "is-bookmarked": o.bookmarked })}
        urn=${vm.urn}
        aria-labelledby="opportunity-detail-title"
      >
        <header class="opportunity-detail-head">
          <h1 id="opportunity-detail-title" class="surface-title" lang="de">${o.title}</h1>
          <p class="opportunity-employer" lang="de">${o.employer}${o.location ? html` · ${o.location}` : nothing}</p>
          <div class="opportunity-chips">
            <span class="opportunity-stage chip" lang="de">${vm.t(STAGE_KEY[o.stage])}</span>
            ${o.fit ? html`<span class="opportunity-fit chip" lang="de">${vm.t(FIT_KEY[o.fit])}</span>` : nothing}
            ${o.jobState ? html`<span class="opportunity-jobstate chip" lang="de">${vm.t(JOB_STATE_KEY[o.jobState])}</span>` : nothing}
            ${o.salary ? html`<span class="opportunity-salary chip" lang="de">${vm.t("opportunity.salary")}: ${o.salary}</span>` : nothing}
          </div>
          ${o.url ? html`<a class="opportunity-posting-link" href=${o.url} target="_blank" rel="noopener noreferrer" lang="de">${vm.t("opportunity.posting.link")}</a>` : nothing}
        </header>

        ${this.briefPanel(vm, o)}
        ${this.matchPanel(vm, o)}
        ${this.orgPanel(vm)}

        <div class="opportunity-actions" role="group">
          ${o.bookmarked
            ? html`<span class="opportunity-saved chip is-bookmarked" lang="de">${vm.t("opportunity.bookmarked")}</span>`
            : html`<button data-act="bookmark" data-opportunity=${o.urn} lang="de">${vm.t("opportunity.bookmark")}</button>`}
          <button data-act="dismiss" data-opportunity=${o.urn} class="opportunity-dismiss" lang="de">${vm.t("opportunity.dismiss")}</button>
          <button data-act="delete" data-opportunity=${o.urn} class="opportunity-remove" lang="de">${vm.t("opportunity.delete")}</button>
        </div>
        ${back}
      </section>
    `
  }

  protected override actions(vm: OpportunityDetailVm): ActionMap {
    return {
      bookmark: () => vm.bookmark(),
      dismiss: () => vm.dismiss(),
      delete: () => vm.remove(),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
